import React, { useContext, useMemo, useState, FC, ReactElement } from "react";
import { useTextDomainContext, useUser } from "@/app/providers";
import { useCompanyBuildings } from "@/hooks";
import { SelectDropdown } from "@/components";
import { MeterDropdownProps } from "./MeterDropdown.types";

interface MeterOption {
  label: string;
  value: string;
}

interface Building {
  address: string;
  [key: string]: any;
}

export const MeterDropdown: FC<MeterDropdownProps> = (
  props: MeterDropdownProps,
): ReactElement => {
  const { user } = useUser();
  const {
    currentTeam: currentTeamDefault,
    currentSubsidiary: currentSubsidiaryDefault,
  } = user || {};
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  //#region UseState
  const [newDefaultValue, setNewDefaultValue] = useState<string | null>(null);
  //#endregion UseState

  //#region useMemo
  const building = useMemo(() => {
    return props.building || "all";
  }, [props.building]);

  const componentLabel = useMemo(() => {
    return props.componentLabel || gettext("Meter");
  }, [gettext, props.componentLabel]);

  const defaultValue = useMemo(() => {
    return newDefaultValue || props.defaultValue || "all";
  }, [newDefaultValue, props.defaultValue]);

  const showAll = useMemo(() => {
    return props.showAll || false;
  }, [props.showAll]);

  const currentTeam = useMemo(() => {
    return props.currentTeam || currentTeamDefault || "";
  }, [currentTeamDefault, props.currentTeam]);

  const currentSubsidiary = useMemo(() => {
    return props.currentSubsidiary || currentSubsidiaryDefault || "";
  }, [currentSubsidiaryDefault, props.currentSubsidiary]);

  const country = useMemo(() => {
    return props.country || "";
  }, [props.country]);

  const zone = useMemo(() => {
    return props.zone || "";
  }, [props.zone]);
  //#endregion useMemo

  //#region Options
  const variables = {
    customerName: currentTeam,
    subsidiaryName: currentSubsidiary,
    country,
    zone,
  };

  const { buildings: buildingData } = useCompanyBuildings(variables);

  const options = useMemo<MeterOption[]>(() => {
    let returnValue: MeterOption[] = [];
    let meters: any[] = [];
    if (props.energyType) {
      if (building === "all" || building?.valueOf() === "all") {
        buildingData?.map((buildingItem: any): any => {
          if (buildingItem[props.energyType!]) {
            meters = [...meters, ...(buildingItem[props.energyType!] as any[])];
          }
          return null;
        });
      } else {
        const firstBuildingValue = buildingData?.find(
          (o: Building) => o.address === building,
        );
        if (firstBuildingValue) {
          meters = [
            ...meters,
            ...((firstBuildingValue as any)[props.energyType] as any[]),
          ];
        }
      }
    }

    meters.forEach((obj) => {
      if (obj) {
        if (obj.label && obj.meterNo) {
          const meterObj: MeterOption = {
            label: obj.label,
            value: obj.meterNo,
          };
          returnValue.push(meterObj);
        }
      }
    });

    return returnValue;
  }, [building, buildingData, props.energyType]);
  //#endregion Options

  //region Handlers
  const handleChange = (event: any): void => {
    setNewDefaultValue(event);
    if (typeof props.onChange === "function") {
      props.onChange(event);
    }
  };
  //endregion Handlers

  return (
    <SelectDropdown
      options={
        options as unknown as import("@/types/Dropdown.types").DropdownOption[]
      }
      componentLabel={componentLabel}
      defaultValue={defaultValue}
      showAll={showAll}
      required={props.required}
      onChange={handleChange}
      refetch={props.refetch || false}
      refetched={props.refetched as any}
      visualStyle="autoComplete"
      sx={{
        ...props.sx,
      }}
    />
  );
};
